// WHY (#590): dimensions / places editor for one DraftItem. Lives in
//   its own file to keep DraftItemEditRow under the 600 LOC hard cap.
//   The toggle, package-row editor, and item totals all read from the
//   parent's DraftItem; mutations bubble back through onPatch /
//   onPackagesChange so the parent stays the source of truth.

import { useTranslation } from 'react-i18next';
import { Button, Icon, IconButton, Input } from '../../../ui/index.js';
import type { DraftItem, DraftPackageRow } from '../types.js';
import { nextClientPackageId } from '../store.js';
import { computePackageVolumeM3, computeRowVolumeM3, totalsFromDraftRows } from '../packageCalc.js';
import {
  Field,
  Grid,
  formatIntInput,
  formatM3Display,
  parseIntInput,
} from './DraftFormPrimitives.js';
import { formatDraftDecimal } from '../draft-media/decimal.js';
import { useDecimalTextInput } from './useDecimalTextInput.js';

export function makeDraftPackageRow(): DraftPackageRow {
  return {
    id: null,
    clientPackageId: nextClientPackageId(),
    widthCm: 0,
    depthCm: 0,
    heightCm: 0,
    weightKg: 0,
    count: 1,
  };
}

interface DimensionsBlockProps {
  item: DraftItem;
  frozen: boolean;
  dimensionsMode: boolean;
  onToggle: (next: boolean) => void;
  onPackagesChange: (rows: DraftPackageRow[]) => void;
}

export function DimensionsBlock({
  item,
  frozen,
  dimensionsMode,
  onToggle,
  onPackagesChange,
}: DimensionsBlockProps) {
  const { t } = useTranslation(['requests']);
  const toggleId = `dimensions-toggle-${item.id}`;
  function patchRow(index: number, patch: Partial<DraftPackageRow>): void {
    onPackagesChange(item.packages.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }
  function removeRow(index: number): void {
    onPackagesChange(item.packages.filter((_, i) => i !== index));
  }
  function addRow(): void {
    onPackagesChange([...item.packages, makeDraftPackageRow()]);
  }
  return (
    <div
      style={{
        borderTop: '1px dashed var(--border-2)',
        paddingTop: 10,
        display: 'grid',
        gap: 10,
      }}
    >
      <label
        htmlFor={toggleId}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 12,
          color: 'var(--fg-2)',
          cursor: frozen ? 'default' : 'pointer',
        }}
      >
        <input
          id={toggleId}
          type="checkbox"
          checked={dimensionsMode}
          disabled={frozen}
          onChange={(e) => onToggle(e.currentTarget.checked)}
        />
        {t('requests:draft.packages.toggle')}
      </label>
      {dimensionsMode && (
        <>
          <div style={{ fontSize: 11, color: 'var(--fg-3)' }}>
            {t('requests:draft.packages.hint')}
          </div>
          {item.packages.length === 0 ? (
            <div
              style={{
                fontSize: 12,
                color: 'var(--status-warn-fg)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Icon name="alert-triangle" size={12} />
              {t('requests:draft.packages.emptyPlaces')}
            </div>
          ) : (
            item.packages.map((row, idx) => (
              <PackageRowEditor
                key={row.clientPackageId}
                index={idx}
                row={row}
                frozen={frozen}
                onPatch={(patch) => patchRow(idx, patch)}
                onRemove={() => removeRow(idx)}
              />
            ))
          )}
          <div>
            <Button kind="ghost" size="sm" icon="plus" disabled={frozen} onClick={() => addRow()}>
              {t('requests:draft.packages.addPlace')}
            </Button>
          </div>
          <DimensionsTotals item={item} />
        </>
      )}
    </div>
  );
}

interface PackageRowEditorProps {
  index: number;
  row: DraftPackageRow;
  frozen: boolean;
  onPatch: (patch: Partial<DraftPackageRow>) => void;
  onRemove: () => void;
}

function PackageRowEditor({ index, row, frozen, onPatch, onRemove }: PackageRowEditorProps) {
  const { t } = useTranslation(['requests', 'common']);
  const perPackage = computePackageVolumeM3(row);
  const rowVolume = computeRowVolumeM3(row);
  const weightField = useDecimalTextInput(row.weightKg, (weightKg) => onPatch({ weightKg }));
  return (
    <div
      style={{
        border: '1px solid var(--border-2)',
        borderRadius: 'var(--r-1)',
        padding: 8,
        display: 'grid',
        gap: 8,
        background: 'var(--bg-2)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--fg-3)' }}>
          {t('requests:draft.packages.place', { index: index + 1 })}
        </div>
        <div style={{ flex: 1 }} />
        <IconButton
          icon="trash-2"
          size={22}
          label={t('requests:draft.packages.removePlace')}
          onClick={onRemove}
          disabled={frozen}
        />
      </div>
      <Grid minWidth={92}>
        <Field label={t('requests:draft.packages.widthCm')} required>
          <Input
            monospace
            value={formatIntInput(row.widthCm)}
            onChange={(v) => onPatch({ widthCm: parseIntInput(v) })}
            placeholder="0"
            readOnly={frozen}
            ariaLabel={t('requests:draft.packages.widthCm')}
          />
        </Field>
        <Field label={t('requests:draft.packages.depthCm')} required>
          <Input
            monospace
            value={formatIntInput(row.depthCm)}
            onChange={(v) => onPatch({ depthCm: parseIntInput(v) })}
            placeholder="0"
            readOnly={frozen}
            ariaLabel={t('requests:draft.packages.depthCm')}
          />
        </Field>
        <Field label={t('requests:draft.packages.heightCm')} required>
          <Input
            monospace
            value={formatIntInput(row.heightCm)}
            onChange={(v) => onPatch({ heightCm: parseIntInput(v) })}
            placeholder="0"
            readOnly={frozen}
            ariaLabel={t('requests:draft.packages.heightCm')}
          />
        </Field>
        <Field label={t('requests:draft.packages.weightKg')} required>
          <Input
            monospace
            value={weightField.text}
            onChange={weightField.onChange}
            {...(!frozen && { onBlur: weightField.onBlur })}
            placeholder="0"
            readOnly={frozen}
            ariaLabel={t('requests:draft.packages.weightKg')}
          />
        </Field>
        <Field label={t('requests:draft.packages.count')} required>
          <Input
            monospace
            value={formatIntInput(row.count)}
            onChange={(v) => onPatch({ count: parseIntInput(v) })}
            placeholder="1"
            readOnly={frozen}
            ariaLabel={t('requests:draft.packages.count')}
          />
        </Field>
      </Grid>
      <div
        style={{
          display: 'flex',
          gap: 16,
          flexWrap: 'wrap',
          fontSize: 11,
          color: 'var(--fg-3)',
        }}
      >
        <span>
          {t('requests:draft.packages.perPackageVolume')}:{' '}
          <strong>{formatM3Display(perPackage)}</strong>
        </span>
        <span>
          {t('requests:draft.packages.rowVolume')}: <strong>{formatM3Display(rowVolume)}</strong>
        </span>
      </div>
    </div>
  );
}

function DimensionsTotals({ item }: { item: DraftItem }) {
  const { t } = useTranslation(['requests']);
  const totals = totalsFromDraftRows(item.packages);
  return (
    <div
      style={{
        background: 'var(--bg-1)',
        border: '1px solid var(--border-2)',
        borderRadius: 'var(--r-1)',
        padding: 8,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 16,
        fontSize: 12,
        color: 'var(--fg-2)',
      }}
    >
      <strong style={{ fontWeight: 600 }}>{t('requests:draft.packages.itemTotal')}:</strong>
      <span>
        {t('requests:draft.packages.itemTotalWeight', {
          value: formatDraftDecimal(totals.weight) || '0',
        })}
      </span>
      <span>
        {t('requests:draft.packages.itemTotalVolume', { value: formatM3Display(totals.volume) })}
      </span>
      <span>{t('requests:draft.packages.itemTotalQuantity', { value: totals.quantity })}</span>
    </div>
  );
}
